/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Dictionary, isEmpty } from 'lodash';
import { Text } from '@blend/enterprise-components';
import { BlendBridge } from 'blb-types/blendBridge';
import { DashboardSection } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSection';
import { DocumentsCard } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard';
import { DocumentTitles } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { cardWrapperStyles } from 'blb-components/AuthenticatedApp/DashboardPage/SectionCard';
import { UploadedDocuments } from 'blb-components/AuthenticatedApp/DashboardPage/docUtils/UploadedDocuments';
import { DocumentSectionComponents } from 'blb-ui-types/BridgeTypes';
import { getConfig } from 'blb-utils/config';
import * as docUtils from '../common/utils/documents';
import {
  getSignAheadDocStatusTag,
  renderENoteDocumentCard,
  renderReceivedRONClosingDocuments,
  renderSplitReceivedDocs,
} from '../common/utils/renderDocuments';
import {
  DEFAULT_LANDING_PATH,
  getDashboardBanner as getCommonDashboardBanner,
  getSupportContactInfoForPrincipal,
  renderDefaultDashboardOverview,
  renderDefaultProfileSidebar,
  SupportContact,
} from '../common';
import { MeContext } from 'blb-contexts/Me';
import { TabType } from 'blb-components/AuthenticatedApp/ProfilePage';
import { Route } from 'blb-components/AuthenticatedApp/routeConfiguration';
import { LoanInfoRow } from 'blb-components/AuthenticatedApp/DashboardPage/LoanInfoSummary/LoanInfoRow';
import { getRelativeTimeOfIsoString } from 'blb-utils/date';
import { getFiltersForOpsDesk } from 'blb-utils/pipeline';
import { Pipeline } from '@blend/search-client';
import { OpsDeskFilter } from 'blb-components/AuthenticatedApp/PipelinePage/NewPipeline/OpsDeskFilter';
import { FilterParams, OpsDeskFilterParams } from 'blb-constants/pipelineFilter';
import { DocWarnings } from 'blb-components/DocWarnings';
import * as personaDocumentUtils from 'blb-persona/common/utils/documents';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

export { getClosingTypeCopy } from '../common';

export const getSupportContactInfo = (): SupportContact => getSupportContactInfoForPrincipal('ops_desk_agent');

export const styles = StyleSheet.create({
  warningWrapper: {
    display: 'flex',
    marginBottom: '1rem',
    alignItems: 'center',
    gap: '0.5rem',
  },
});

const getRONDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  uploadedDocs: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
) => {
  const dayOfDocsUploaded = docUtils.filterDocsByDesignations(uploadedDocs, docIdToRefMap, ['DAY_OF']);
  const signAheadDocsUploaded = docUtils.filterDocsByDesignations(uploadedDocs, docIdToRefMap, ['SIGN_AHEAD']);
  const eNoteDocs = personaDocumentUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['NOTE']);
  const signAheadStatusTag: JSX.Element | undefined = getSignAheadDocStatusTag(loan);

  const { useNewRONDocumentCards } = getConfig();
  const ronClosingDocumentsCard = useNewRONDocumentCards
    ? renderReceivedRONClosingDocuments(receivedDocs, docIdToRefMap, loan, auditEvents)
    : renderSplitReceivedDocs(receivedDocs, docIdToRefMap, 'Lender RON', auditEvents, signAheadStatusTag);

  return {
    receivedDocComponent: (
      <div>
        <DocWarnings loan={loan} />
        <div className={css(cardWrapperStyles.cardGroup)}>
          {ronClosingDocumentsCard}
          {renderENoteDocumentCard(loan.closing?.eNoteData, eNoteDocs)}
        </div>
      </div>
    ),
    uploadedDocComponent: (
      <div className={css(cardWrapperStyles.cardGroup)}>
        {/* when documents split by ops desk they will be here */}
        {!isEmpty(signAheadDocsUploaded) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              documents={signAheadDocsUploaded}
              headerChildren={signAheadStatusTag}
              id="it-UploadedSignAhead"
              isUploaded={true}
              key="it-UploadedSignAhead"
              title={DocumentTitles.UPLOADED_SIGN_AHEAD_DOCUMENTS}
            />
          </div>
        )}
        {!isEmpty(dayOfDocsUploaded) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              documents={dayOfDocsUploaded}
              id="it-UploadedDayOfDocs"
              isUploaded={true}
              key="it-UploadedDayOfDocs"
              title={DocumentTitles.UPLOADED_DAY_OF_DOCUMENTS}
            />
          </div>
        )}
      </div>
    ),
  };
};

const getHybridDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  uploadedDocs: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
) => {
  const dayOfDocsUploaded = docUtils.filterDocsByDesignations(uploadedDocs, docIdToRefMap, ['DAY_OF']);
  const signAheadDocsUploaded = docUtils.filterDocsByDesignations(uploadedDocs, docIdToRefMap, ['SIGN_AHEAD']);
  const eNoteDocs = personaDocumentUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['NOTE']);
  const signAheadStatusTag: JSX.Element | undefined = getSignAheadDocStatusTag(loan);
  return {
    receivedDocComponent: (
      <div>
        <DocWarnings loan={loan} />
        <div className={css(cardWrapperStyles.cardGroup)}>
          {renderSplitReceivedDocs(receivedDocs, docIdToRefMap, 'Ink Sign', auditEvents, signAheadStatusTag)}
          {renderENoteDocumentCard(loan.closing?.eNoteData, eNoteDocs)}
        </div>
      </div>
    ),
    uploadedDocComponent: (
      <div className={css(cardWrapperStyles.cardGroup)}>
        {/* when documents split by ops desk they will be here */}
        {!isEmpty(signAheadDocsUploaded) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              documents={signAheadDocsUploaded}
              headerChildren={signAheadStatusTag}
              id="it-UploadedSignAhead"
              isUploaded={true}
              key="it-UploadedSignAhead"
              title={DocumentTitles.UPLOADED_SIGN_AHEAD_DOCUMENTS}
            />
          </div>
        )}
        {!isEmpty(dayOfDocsUploaded) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              documents={dayOfDocsUploaded}
              id="it-UploadedDayOfDocs"
              isUploaded={true}
              key="it-UploadedDayOfDocs"
              title={DocumentTitles.UPLOADED_DAY_OF_DOCUMENTS}
            />
          </div>
        )}
      </div>
    ),
  };
};

const getTraditionalAndUndeterminedClosingDocumentSectionComponents = (
  me: BlendBridge.API.User,
  receivedDocs: BlendBridge.API.Document[],
  uploadedDocs: BlendBridge.API.Document[],
  removeDoc: (id: string) => void,
  auditEvents?: EClose.AuditEventResponse[],
): DocumentSectionComponents => {
  return {
    receivedDocComponent: (
      <div className={css(cardWrapperStyles.cardGroup)}>
        {!isEmpty(receivedDocs) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              auditEvents={auditEvents}
              documents={receivedDocs}
              id="it-LenderInkSign"
              isUploaded={false}
              key="it-LenderInkSign"
              title={DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS}
            />
          </div>
        )}
      </div>
    ),
    uploadedDocComponent: (
      <UploadedDocuments documents={uploadedDocs} removeDoc={removeDoc} uploadedById={me.principalId} />
    ),
  };
};

// exported for tests only
export const getDocumentSectionConfigs = (
  me: BlendBridge.API.User,
  loan: BlendBridge.API.Loan,
  loanDocuments: BlendBridge.API.Document[],
  removeDoc: (id: string) => void,
): DashboardSection.Props['sectionConfigs'] => {
  const closingType = loan.closing?.ronSelectionData?.derivedClosingType || loan.closing?.closingType;
  const auditEvents = loan.closing?.auditEvents;
  const allClosingDocumentReferences = docUtils.getAllClosingDocumentReferences(
    loan.closing?.documentReferences,
    auditEvents,
  );
  const docIdToRefMap = docUtils.convertDocumentReferencesToDocIdToRefMap(allClosingDocumentReferences);
  const receivedDocs = docUtils.getReceivedClosingDocuments(me, loanDocuments);
  const uploadedDocs = docUtils.filterDocsByUploader(loanDocuments, me.principalId);

  const { receivedDocComponent, uploadedDocComponent } = (() => {
    switch (closingType) {
      case 'HYBRID': {
        return getHybridDocumentSectionComponents(receivedDocs, docIdToRefMap, uploadedDocs, loan, auditEvents);
      }
      case 'RON': {
        return getRONDocumentSectionComponents(receivedDocs, docIdToRefMap, uploadedDocs, loan, auditEvents);
      }
      case 'TRADITIONAL':
      default: {
        return getTraditionalAndUndeterminedClosingDocumentSectionComponents(
          me,
          receivedDocs,
          uploadedDocs,
          removeDoc,
          auditEvents,
        );
      }
    }
  })();

  return [
    {
      content: isEmpty(receivedDocs) ? <Text>No documents yet.</Text> : receivedDocComponent,
      title: 'Received Documents',
    },
    {
      content: isEmpty(uploadedDocs) ? <Text>Nothing uploaded yet.</Text> : uploadedDocComponent,
      title: 'Uploaded Documents',
    },
  ];
};

export const renderDashboardOverview = (
  dashboardSections: DashboardSectionItem.SectionConfig[],
  loan: BlendBridge.API.Loan,
  documents: BlendBridge.API.Document[],
  removeDoc: (id: string) => void,
  setInstructionOpen: (val: boolean) => void,
  areInstructionsOpen: boolean,
): React.ReactElement =>
  renderDefaultDashboardOverview(
    dashboardSections,
    loan,
    getDocumentSectionConfigs(React.useContext(MeContext)!, loan, documents, removeDoc),
    setInstructionOpen,
    areInstructionsOpen,
  );

export const getProfileTabs = (_me: BlendBridge.API.User): TabType[] => ['Profile'];

export const getPipelinePollingInterval = (): number => 1000 * 10;

export const renderProfileSidebar = renderDefaultProfileSidebar;

export const showSpecialInstructions = (): boolean => true;

export const getAllowedRoutes = (_me: BlendBridge.API.User): { allowedRoutes: Route[]; defaultLandingPath: Route } => {
  return {
    allowedRoutes: ['/pipeline', '/:loanId/dashboard', '/profile', '/stats', '/registration'],
    defaultLandingPath: DEFAULT_LANDING_PATH,
  };
};

export const getLastSentAtRow = (closing: EClose.FullClosing): React.ReactElement => {
  return (
    <LoanInfoRow
      iconType="timer"
      rowHeader="Last Sent"
      rowInfo={closing.lastSentAt ? getRelativeTimeOfIsoString(closing.lastSentAt) ?? 'Check debug' : 'Not sent'}
    />
  );
};

export const getDashboardBanner = (
  me: BlendBridge.API.User,
  loan: BlendBridge.API.Loan,
): BlendBridge.IAlert | undefined => {
  const banner = getCommonDashboardBanner(me, loan);
  if (banner) {
    return banner;
  }
  if (loan.closing?.closingStatus === 'DRAFT') {
    return {
      message: `This loan has not been sent yet. You may see incomplete data.`,
      theme: 'warn',
    };
  }
  if (loan.currentAssignee && loan.currentAssignee.principalId !== me.principalId) {
    return {
      message: `Someone else (${loan.currentAssignee.name ?? ''}) is assigned to this loan.`,
      theme: 'warn',
    };
  }
  return;
};

export const getPipelineFilters = (filterParams: FilterParams): Pipeline.IFilter[] => {
  return getFiltersForOpsDesk(filterParams as OpsDeskFilterParams);
};

export const renderPipelineFilter = (
  filters: OpsDeskFilterParams,
  onFilterChanged: (filterParams: FilterParams) => void,
) => <OpsDeskFilter filters={filters} onFilterChanged={onFilterChanged} />;
